import { noop } from 'lodash'
import Button from './Button'
import {
  SvgPagLeftArrowOne,
  SvgPagLeftArrowTwo,
  SvgPagRightArrowOne,
  SvgPagRightArrowTwo,
} from '../assets/images'
import { useEffect, useState } from 'react'
import {
  FIELD_VALUE_CONSTANTS,
  NAMESPACE,
  PAGINATION_NAMESPACE,
} from '../utils'
interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (
    page: number,
    rowsPerPage: number,
    pageChangeMethod?: string
  ) => void
  onRowsChange?: (rowsPerPage: number) => void
  rowsPer?: number
  totalElements?: number
  numberOfElements?: number
  tabIndex?: number
  hidePagination?: boolean
}
const Pagination: React.FC<PaginationProps> = ({
  totalElements,
  numberOfElements,
  currentPage,
  totalPages,
  onPageChange,
  onRowsChange,
  rowsPer = FIELD_VALUE_CONSTANTS.TWENTY_FIVE,
  tabIndex,
  hidePagination = false,
}) => {
  const isPaginationPage = true
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPer)
  const generatePages = () => {
    const pages: (number | string)[] = []
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      pages.push(1)
      if (currentPage <= 4) {
        for (let i = 2; i <= 5; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      } else if (currentPage > 4 && currentPage < totalPages - 3) {
        pages.push('...')
        pages.push(currentPage - 1)
        pages.push(currentPage)
        pages.push(currentPage + 1)
        pages.push('...')
        pages.push(totalPages)
      } else {
        pages.push('...')
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i)
        }
      }
    }
    return pages
  }
  const pages = generatePages()
  const handleRowsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e?.target?.value))
    if (onRowsChange) onRowsChange(parseInt(e?.target?.value))
  }
  const handlePrevAllClick = () => {
    onPageChange(1, rowsPerPage, 'Double arrow')
  }
  const handlePrevClick = () => {
    onPageChange(currentPage - 1, rowsPerPage, 'Single arrow')
  }
  const handleNextClick = () => {
    onPageChange(currentPage + 1, rowsPerPage, 'Single arrow')
  }
  const handleNextAllClick = () => {
    onPageChange(totalPages, rowsPerPage, 'Double arrow')
  }
  const handleCurrentClick = (page: number) => {
    onPageChange(page, rowsPerPage, 'Number buttons')
  }
  useEffect(() => {
    onPageChange(currentPage, rowsPerPage)
  }, [rowsPerPage])
  useEffect(() => {
    setRowsPerPage(rowsPer)
  }, [rowsPer])
  return (
    <div
      className={`${
        hidePagination ? 'hidden' : ''
      } font-primary flex items-center justify-center bg-secondary-25 p-1.5 gap-2`}
    >
      <div className="flex h-8 py-0">
        <Button
          type="button"
          variant="secondary"
          size="small"
          onClick={handlePrevAllClick}
          disabled={currentPage === 1}
          isPaginationPage={isPaginationPage}
          className={`py-0 px-1 rounded-lg flex items-center justify-center ${
            currentPage === 1
              ? 'cursor-not-allowed border-none'
              : 'cursor-pointer border-2'
          }`}
          tabIndex={tabIndex}
        >
          <SvgPagLeftArrowOne
            className={`${
              currentPage === 1 ? 'fill-accent-700' : 'fill-black'
            }`}
          />
        </Button>
      </div>
      <div className="flex h-8">
        <Button
          type="button"
          size="small"
          variant="secondary"
          onClick={handlePrevClick}
          disabled={currentPage === 1}
          isPaginationPage={isPaginationPage}
          className={`px-1 rounded-lg font-light text-center flex items-center justify-center ${
            currentPage === 1
              ? 'cursor-not-allowed border-none'
              : 'cursor-pointer border-2'
          }`}
          tabIndex={tabIndex}
        >
          <SvgPagLeftArrowTwo
            className={`${
              currentPage === 1 ? 'fill-accent-700' : 'fill-black'
            }`}
          />
        </Button>
      </div>
      {pages.map((page, index) => (
        <div className="flex h-8" key={`${page}-${index}`}>
          {typeof page === 'number' ? (
            <Button
              type="button"
              variant={`${currentPage === page ? 'primary' : 'secondary'}`}
              size="small"
              onClick={() => handleCurrentClick(page)}
              className={`px-1 w-8 h-8 cursor-pointer text-center flex items-center justify-center rounded-lg font-light ${
                currentPage === page ? '' : 'border-none bg-none'
              }`}
              isPaginationPage={isPaginationPage}
              tabIndex={tabIndex}
            >
              <span
                className={`text-xs font-primary font-thin${
                  currentPage === page ? '' : 'text-accent-700'
                }`}
              >
                {page}
              </span>
            </Button>
          ) : (
            <Button
              type="button"
              variant={'secondary'}
              size="small"
              onClick={noop}
              isPaginationPage={isPaginationPage}
              className={`px-1 w-8 h-8 rounded-lg cursor-not-allowed text-center flex items-center justify-center border-none bg-none`}
              tabIndex={tabIndex}
            >
              <span
                className={`text-[0.813rem] font-thin text-accent-700 font-primary`}
              >
                ...
              </span>
            </Button>
          )}
        </div>
      ))}
      <div className="flex h-8">
        <Button
          type="button"
          variant="secondary"
          size="small"
          onClick={handleNextClick}
          disabled={currentPage === totalPages}
          isPaginationPage={isPaginationPage}
          className={`px-1 rounded-lg text-center flex items-center justify-center ${
            currentPage === totalPages
              ? 'cursor-not-allowed border-none'
              : 'cursor-pointer border-2'
          }`}
          tabIndex={tabIndex}
        >
          <SvgPagRightArrowOne
            className={`${
              currentPage === 1 ? 'fill-accent-700' : 'fill-black'
            }`}
          />
        </Button>
      </div>
      <div className="flex h-8">
        <Button
          type="button"
          variant="secondary"
          size="small"
          onClick={handleNextAllClick}
          disabled={currentPage === totalPages}
          isPaginationPage={isPaginationPage}
          className={`px-1 rounded-lg text-center flex items-center justify-center ${
            currentPage === totalPages
              ? 'cursor-not-allowed border-none'
              : 'cursor-pointer border-2'
          }`}
          tabIndex={tabIndex}
        >
          <SvgPagRightArrowTwo
            className={`${
              currentPage === 1 ? 'fill-accent-700' : 'fill-black'
            }`}
          />
        </Button>
      </div>
      <div className="flex gap-2 items-center h-[1.461rem] w-[7.068rem]">
        <span className="text-xs font-light leading-4.5 ">
          {PAGINATION_NAMESPACE.SHOW}{' '}
        </span>
        <select
          name="paginatedDropdown"
          className="flex items-center justify-center w-[4.202rem] h-8 text-xs bg-white rounded-md font-light"
          id="paginatedDropdown"
          onChange={handleRowsChange}
          defaultValue={rowsPerPage.toString()}
          value={rowsPerPage?.toString()}
          tabIndex={tabIndex}
        >
          <option
            value="5"
            className="text-xs flex items-center justify-center w-[4.202rem] h-8"
          >
            {NAMESPACE.ROWS.FIVE}
          </option>
          <option
            value="10"
            className="text-xs flex items-center justify-center w-[4.202rem] h-8"
          >
            {NAMESPACE?.ROWS?.TEN}
          </option>
          <option
            value="25"
            className="text-xs flex items-center justify-center w-[4.202rem] h-8"
          >
            {NAMESPACE?.ROWS?.TWENTY_FIVE}
          </option>
          <option
            value="50"
            className="text-xs flex items-center justify-center w-[4.202rem] h-8"
          >
            {NAMESPACE?.ROWS?.FIFTY}
          </option>
          <option
            value="100"
            className="text-xs flex items-center justify-center w-[4.202rem] h-8"
          >
            {NAMESPACE?.ROWS?.HUNDRED}
          </option>
        </select>
      </div>
      {totalElements && numberOfElements ? (
        <div className="text-xs font-extralight">
          {numberOfElements} /{totalElements} {PAGINATION_NAMESPACE.ROWS}
        </div>
      ) : null}
    </div>
  )
}
export default Pagination
